import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import { Carer } from '../../models/adhoc-service.model';

export interface StaffSelectDialogData {
  staff: Carer[];
  requiredRoleIds?: string[];
}

// RS role ID to display name mapping
const ROLE_NAME_MAP: Record<string, string> = {
  '111': 'PCW',
  '34': 'RN',
  '129': 'HPAC',
  '106': 'Allied Health',
  '116': 'CBM'
};

@Component({
  selector: 'app-staff-select-dialog',
  templateUrl: './staff-select-dialog.component.html',
  styleUrls: ['./staff-select-dialog.component.scss']
})
export class StaffSelectDialogComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  searchControl = new FormControl<string>('');
  allStaff: Carer[] = [];
  filteredStaff: Carer[] = [];
  selectedStaff: Carer | null = null;

  allRoles: string[] = [];
  activeRoleFilters: Set<string> = new Set();
  rolesLocked = false;

  private lastClickTime = 0;
  private lastClickedId: string | null = null;
  private readonly DOUBLE_CLICK_DELAY = 400;

  constructor(
    public dialogRef: MatDialogRef<StaffSelectDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: StaffSelectDialogData
  ) {}

  ngOnInit(): void {
    this.allStaff = [...this.data.staff].sort((a, b) =>
      `${a.FirstName} ${a.LastName}`.localeCompare(`${b.FirstName} ${b.LastName}`, undefined, { sensitivity: 'base' })
    );

    // Collect all unique roles across staff
    const roleSet = new Set<string>();
    for (const s of this.allStaff) {
      for (const r of (s.rs_role_ids || [])) {
        roleSet.add(r);
      }
    }
    this.allRoles = [...roleSet].sort();

    // If required roles are provided, lock those as active filters
    if (this.data.requiredRoleIds && this.data.requiredRoleIds.length > 0) {
      this.rolesLocked = true;
      for (const r of this.data.requiredRoleIds) {
        this.activeRoleFilters.add(r);
      }
    }

    this.applyFilters();

    this.searchControl.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => this.applyFilters());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleRole(role: string): void {
    if (this.rolesLocked) return;
    if (this.activeRoleFilters.has(role)) {
      this.activeRoleFilters.delete(role);
    } else {
      this.activeRoleFilters.add(role);
    }
    this.applyFilters();
  }

  isRoleActive(role: string): boolean {
    return this.activeRoleFilters.has(role);
  }

  private applyFilters(): void {
    let result = this.allStaff;

    // Filter by active roles
    if (this.activeRoleFilters.size > 0) {
      result = result.filter(s =>
        (s.rs_role_ids || []).some(r => this.activeRoleFilters.has(r))
      );
    }

    // Filter by search text
    const term = (this.searchControl.value || '').toLowerCase().trim();
    if (term) {
      result = result.filter(s =>
        s.FirstName.toLowerCase().includes(term) ||
        s.LastName.toLowerCase().includes(term)
      );
    }

    this.filteredStaff = result;
  }

  getRoles(staff: Carer): string {
    return (staff.rs_role_ids || [])
      .map(id => ROLE_NAME_MAP[id] || id)
      .join(', ');
  }

  getRoleName(roleId: string): string {
    return ROLE_NAME_MAP[roleId] || roleId;
  }

  onStaffClick(staff: Carer): void {
    const now = Date.now();
    const isDoubleClick = (now - this.lastClickTime) < this.DOUBLE_CLICK_DELAY
      && this.lastClickedId === staff.CarerID;
    this.lastClickTime = now;
    this.lastClickedId = staff.CarerID;

    if (isDoubleClick) {
      this.dialogRef.close(staff);
      return;
    }

    this.selectedStaff = staff;
  }

  onSelect(): void {
    if (this.selectedStaff) {
      this.dialogRef.close(this.selectedStaff);
    }
  }

  isSelected(staff: Carer): boolean {
    return this.selectedStaff?.CarerID === staff.CarerID;
  }
}
